import { PageCard } from '@common/components/layout';
import { TableSkeleton } from '@common/components/skeleton/TableSkeleton';
import { useApp } from '@common/hooks';
import { AuthOperationUINames } from '@common/models/Enums/AuthOperationStrings';
import { SignatureStore, VeterinarianStore } from '@hospital/stores';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { UserLocations, UserProfileDetail, UserProfileLicenses, UserProfileRoles, UserProfileSignature, UserSchedulePreferences } from '.';
import { UserInactiveProfileBanner } from './UserInactiveProfileBanner';
import { UserProfileCommunicationTemplates } from './UserProfileCommunicationTemplates';
import { UserStore } from './UserStore';


export interface IUserProfileProps {
    store: UserStore;
    vetStore: VeterinarianStore;
}

export const UserProfile = observer(({ store, vetStore }: IUserProfileProps) => {
    const { configuration } = useApp();
    const signatureStore = new SignatureStore({ userStore: store });
    useEffect(() => {
        if (store.vet?.veterinarianId) {
            vetStore.fetchVeterinarianByVetId(store.vet.veterinarianId)
        }
    }, [store.vet, vetStore])

    const currentUserIsThisVet = vetStore.veterinarian?.petcoUserId == configuration.userIdentityName.split('@')[0];
    const canEditUserRoles = configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditUserRoles);
    const canEditUserLocations = configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditUserLocations);
    const canEditDvmLicenses = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditDvmLicenses);
    const canEditDvmSchedule = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditDvmSchedule);
    const canEditDvmSignature = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditDvmSignature);
    const canEditProviderCommunicationTemplates = currentUserIsThisVet || configuration.HasUIFlag(AuthOperationUINames.UI_HospitalFlags_CanEditProviderCommunicationTemplates)

    return (
        <>
            <Row>
                <Col md={12}>
                    <UserInactiveProfileBanner isProfileActive={store.user?.isProfileActive} />
                </Col>
            </Row>
            <Row>
                <Col md={7}>
                    <div className="f-flex flex-column">
                        <PageCard title="User Details">
                            <UserProfileDetail user={store.user} />
                        </PageCard>
                        <PageCard className="mt-md-4" title="Roles">
                            <UserProfileRoles store={store} readOnly={!canEditUserRoles} />
                        </PageCard>

                        {store.hasVetRole && !vetStore.loading && <>
                            <PageCard className="mt-md-4" title="Licenses and Registrations">
                                <UserProfileLicenses store={store} readOnly={!canEditDvmLicenses} />
                            </PageCard>
                            <PageCard className="mt-md-4" title="Schedule Preferences">
                                {vetStore.loading && <TableSkeleton rows={12} cols={7} />}
                                {!vetStore.loading && vetStore.veterinarian &&
                                    <UserSchedulePreferences
                                        readOnly={!canEditDvmSchedule}
                                        scheduleBlockTypes={configuration.scheduleBlockTypes.filter(e => e.isOverrideType === false)}
                                        veterinarian={vetStore.veterinarian}
                                        onSavePreferences={(schedulePreferences, schedulingColor) => vetStore.saveSchedulePreferences({ schedulePreferences, schedulingColor })}
                                        isActive={store.isActive} />
                                }
                            </PageCard>
                        </>}
                    </div>
                </Col>
                <Col md={5}>
                    <div className="f-flex flex-column ps-4">
                        <PageCard title="Locations">
                            <UserLocations store={store} readOnly={!canEditUserLocations} />
                        </PageCard>

                        {store.hasVetRole && !vetStore.loading &&
                            <PageCard className="mt-md-4" title="Signature">
                                <UserProfileSignature store={signatureStore} readOnly={!canEditDvmSignature} isActive={store.isActive} />
                            </PageCard>
                        }

                        {store.hasVetRole && !vetStore.loading &&
                            <PageCard className="mt-md-4" title="Hospital Communication Templates">
                                <UserProfileCommunicationTemplates store={store} readOnly={!canEditProviderCommunicationTemplates} />
                            </PageCard>
                        }
                    </div>
                </Col>
            </Row>

        </>
    )
});
